/**
 * useMobileIconDrag - iPhone-style long-press-to-drag icon reordering
 *
 * Manages jiggle mode, drag tracking, sparse grid cell targeting,
 * page edge scrolling, and dock drag interactions for the mobile icon grid.
 *
 * Supports sparse grids: icons can be placed at any (page, row, col) cell,
 * dropping on empty cells places the icon, dropping on occupied cells swaps.
 *
 * Performance: drag position uses refs + direct DOM manipulation to avoid
 * React re-renders on every touch move. Hover cell is throttled via rAF.
 */

import { RefObject, useCallback, useRef, useState } from "react";
import type {
  IconGroup,
  MobileGridPosition,
  MobileDockPosition,
} from "@repo/types";

export interface UseMobileIconDragOptions {
  mobileGridPositions: Record<string, MobileGridPosition>;
  mobileDockPositions: Record<string, MobileDockPosition>;
  currentPage: number;
  setCurrentPage: (page: number | ((p: number) => number)) => void;
  totalPages: number;
  cols: number;
  rows: number;
  pageContainerRef: RefObject<HTMLDivElement | null>;
  dockContainerRef: RefObject<HTMLDivElement | null>;
  onPlaceInGrid: (
    iconId: string,
    page: number,
    row: number,
    col: number
  ) => void;
  onPlaceInDock: (iconId: string, col: number) => void;
  onContextMenu?: (
    iconId: string,
    source: "grid" | "dock",
    x: number,
    y: number
  ) => void;
  onDismissContextMenu?: () => void;
  /** Icon groups for detecting group hover targets */
  iconGroups?: Record<string, IconGroup>;
  /** Called when an icon is dropped on another icon after dwell (creates a group) */
  onCreateGroup?: (
    iconIdA: string,
    iconIdB: string,
    position:
      | { type: "grid"; page: number; row: number; col: number }
      | { type: "dock"; col: number }
  ) => string;
  /** Called when an icon is dropped on an existing group after dwell */
  onAddToGroup?: (groupId: string, iconId: string) => void;
}

export interface UseMobileIconDragReturn {
  isJiggling: boolean;
  draggedIconId: string | null;
  /** The grid cell currently hovered during drag */
  hoverCell: { page: number; row: number; col: number } | null;
  /** The dock column currently hovered during drag (null = not over dock) */
  hoverDockCol: number | null;
  dragSource: "grid" | "dock" | null;
  /** Attach this ref to the drag ghost element */
  ghostRef: RefObject<HTMLDivElement | null>;
  /** Initial ghost position (for mounting) */
  ghostInitial: { x: number; y: number } | null;
  /** Whether the ghost is snapping to its target position */
  isSnapping: boolean;
  exitJiggleMode: () => void;
  handleIconTouchStart: (
    iconId: string,
    source: "grid" | "dock",
    e: React.TouchEvent
  ) => void;
  /** Always bind this — internally no-ops when not dragging */
  handleTouchMove: (e: React.TouchEvent) => void;
  /** Always bind this — internally no-ops when not dragging */
  handleTouchEnd: () => void;
  handleBackgroundTap: () => void;
  /** ID of icon/group being hovered long enough to trigger group creation */
  groupHoverTarget: string | null;
}

const CONTEXT_MENU_DELAY = 300;
const JIGGLE_DELAY = 800;
const LONG_PRESS_MOVE_THRESHOLD = 10;
const PAGE_EDGE_ZONE = 40;
const PAGE_EDGE_DWELL = 400;

export function useMobileIconDrag({
  mobileGridPositions,
  mobileDockPositions,
  currentPage,
  setCurrentPage,
  totalPages,
  cols,
  rows,
  pageContainerRef,
  dockContainerRef,
  onPlaceInGrid,
  onPlaceInDock,
  onContextMenu,
  onDismissContextMenu,
  iconGroups,
  onCreateGroup,
  onAddToGroup,
}: UseMobileIconDragOptions): UseMobileIconDragReturn {
  const [isJiggling, setIsJiggling] = useState(false);
  const [draggedIconId, setDraggedIconId] = useState<string | null>(null);
  const [hoverCell, setHoverCell] = useState<{
    page: number;
    row: number;
    col: number;
  } | null>(null);
  const [hoverDockCol, setHoverDockCol] = useState<number | null>(null);
  const [dragSource, setDragSource] = useState<"grid" | "dock" | null>(null);
  const [isSnapping, setIsSnapping] = useState(false);

  // Ghost element ref — positioned via direct DOM manipulation for 60fps
  const ghostRef = useRef<HTMLDivElement>(null);

  // Refs for two-phase long-press detection (context menu → jiggle)
  const contextMenuTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const jiggleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contextMenuShownRef = useRef(false);
  const touchStartPos = useRef({ x: 0, y: 0 });

  // Ref-based drag tracking (works before React re-renders)
  const activeDragRef = useRef<{
    iconId: string;
    source: "grid" | "dock";
  } | null>(null);

  // Current drag position (ref, not state — avoids re-renders)
  const dragPosRef = useRef({ x: 0, y: 0 });
  // Initial position for ghost mount (state, only set once per drag)
  const [ghostInitial, setGhostInitial] = useState<{
    x: number;
    y: number;
  } | null>(null);

  // Refs for page edge scrolling
  const edgeScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const edgeDirection = useRef<"left" | "right" | null>(null);

  // Cached rects
  const gridRectRef = useRef<DOMRect | null>(null);
  const dockRectRef = useRef<DOMRect | null>(null);

  // rAF handle for throttled cell updates
  const rafRef = useRef<number | null>(null);

  // Group dwell detection — tracks when dragged icon hovers over an occupied cell
  const groupDwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const groupDwellCellRef = useRef<string | null>(null); // "page:row:col" or "dock:col"
  const [groupHoverTarget, setGroupHoverTarget] = useState<string | null>(null);

  const iconGroupsRef = useRef(iconGroups);
  iconGroupsRef.current = iconGroups;

  // Latest values in refs for stable callbacks
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const gridPositionsRef = useRef(mobileGridPositions);
  gridPositionsRef.current = mobileGridPositions;
  const dockPositionsRef = useRef(mobileDockPositions);
  dockPositionsRef.current = mobileDockPositions;

  const clearLongPress = useCallback(() => {
    if (contextMenuTimer.current) {
      clearTimeout(contextMenuTimer.current);
      contextMenuTimer.current = null;
    }
    if (jiggleTimer.current) {
      clearTimeout(jiggleTimer.current);
      jiggleTimer.current = null;
    }
  }, []);

  const clearGroupDwell = useCallback(() => {
    if (groupDwellTimerRef.current) {
      clearTimeout(groupDwellTimerRef.current);
      groupDwellTimerRef.current = null;
    }
    groupDwellCellRef.current = null;
    setGroupHoverTarget(null);
  }, []);

  const clearEdgeScroll = useCallback(() => {
    if (edgeScrollTimer.current) {
      clearTimeout(edgeScrollTimer.current);
      edgeScrollTimer.current = null;
    }
    edgeDirection.current = null;
  }, []);

  const exitJiggleMode = useCallback(() => {
    setIsJiggling(false);
    setDraggedIconId(null);
    setGhostInitial(null);
    setHoverCell(null);
    setHoverDockCol(null);
    setDragSource(null);
    activeDragRef.current = null;
    contextMenuShownRef.current = false;
    clearLongPress();
    clearEdgeScroll();
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, [clearLongPress, clearEdgeScroll]);

  const updateGhostPosition = useCallback((x: number, y: number) => {
    dragPosRef.current = { x, y };
    const ghost = ghostRef.current;
    if (ghost) {
      ghost.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) scale(1.1)`;
    }
  }, []);

  const startDrag = useCallback(
    (iconId: string, source: "grid" | "dock", x: number, y: number) => {
      activeDragRef.current = { iconId, source };

      setDraggedIconId(iconId);
      setDragSource(source);

      if (source === "grid") {
        const pos = gridPositionsRef.current[iconId];
        if (pos) {
          setHoverCell({ page: pos.page, row: pos.row, col: pos.col });
        }
      } else {
        const pos = dockPositionsRef.current[iconId];
        if (pos) {
          setHoverDockCol(pos.col);
        }
      }

      // Set initial ghost position (state — for first render mount)
      dragPosRef.current = { x, y };
      setGhostInitial({ x, y });

      // Cache rects
      if (pageContainerRef.current) {
        gridRectRef.current = pageContainerRef.current.getBoundingClientRect();
      }
      if (dockContainerRef.current) {
        dockRectRef.current = dockContainerRef.current.getBoundingClientRect();
      }
    },
    [pageContainerRef, dockContainerRef]
  );

  const handleIconTouchStart = useCallback(
    (iconId: string, source: "grid" | "dock", e: React.TouchEvent) => {
      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      contextMenuShownRef.current = false;

      if (isJiggling) {
        clearLongPress();
        startDrag(iconId, source, touch.clientX, touch.clientY);
        return;
      }

      clearLongPress();

      // Phase 1: Show context menu at 300ms
      contextMenuTimer.current = setTimeout(() => {
        contextMenuTimer.current = null;
        contextMenuShownRef.current = true;
        if (navigator.vibrate) {
          navigator.vibrate(50);
        }
        onContextMenu?.(iconId, source, touch.clientX, touch.clientY);
      }, CONTEXT_MENU_DELAY);

      // Phase 2: Escalate to jiggle mode at 800ms
      jiggleTimer.current = setTimeout(() => {
        jiggleTimer.current = null;
        contextMenuShownRef.current = false;
        onDismissContextMenu?.();
        setIsJiggling(true);
        startDrag(iconId, source, touch.clientX, touch.clientY);
      }, JIGGLE_DELAY);
    },
    [isJiggling, clearLongPress, startDrag, onContextMenu, onDismissContextMenu]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      const touch = e.touches[0];
      const x = touch.clientX;
      const y = touch.clientY;

      // Cancel long-press if moved too far
      if (contextMenuTimer.current || jiggleTimer.current) {
        const dx = x - touchStartPos.current.x;
        const dy = y - touchStartPos.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > LONG_PRESS_MOVE_THRESHOLD) {
          clearLongPress();
        }
      }

      const active = activeDragRef.current;
      if (!active) return;

      // Update ghost position directly (no state, no re-render)
      updateGhostPosition(x, y);

      // Throttle cell calculations to rAF
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        const pos = dragPosRef.current;

        // Check if over dock
        const dockRect = dockRectRef.current;
        if (dockRect && pos.y >= dockRect.top - 20) {
          // Calculate dock column
          const dockCol = Math.max(
            0,
            Math.min(
              cols - 1,
              Math.floor((pos.x - dockRect.left) / (dockRect.width / cols))
            )
          );
          setHoverDockCol(dockCol);
          setHoverCell(null);
          clearEdgeScroll();
          return;
        }
        setHoverDockCol(null);

        // Calculate grid cell position
        const gridRect = gridRectRef.current;
        if (gridRect) {
          const cellWidth = gridRect.width / cols;
          const cellHeight = gridRect.height / rows;

          const col = Math.max(
            0,
            Math.min(cols - 1, Math.floor((pos.x - gridRect.left) / cellWidth))
          );
          const row = Math.max(
            0,
            Math.min(rows - 1, Math.floor((pos.y - gridRect.top) / cellHeight))
          );
          setHoverCell({ page: currentPageRef.current, row, col });

          // Group dwell detection: check if hovering over an occupied cell
          const cellKey = `${currentPageRef.current}:${row}:${col}`;
          const gridPos = gridPositionsRef.current;
          const dragId = activeDragRef.current?.iconId;
          const occupantId = Object.entries(gridPos).find(
            ([id, p]) =>
              id !== dragId &&
              p.page === currentPageRef.current &&
              p.row === row &&
              p.col === col
          )?.[0];

          if (occupantId && cellKey !== groupDwellCellRef.current) {
            // New occupied cell — start dwell timer
            if (groupDwellTimerRef.current) {
              clearTimeout(groupDwellTimerRef.current);
            }
            groupDwellCellRef.current = cellKey;
            const isTargetGroup = !!(iconGroupsRef.current ?? {})[occupantId];
            const dwellTime = isTargetGroup ? 300 : 500;
            groupDwellTimerRef.current = setTimeout(() => {
              groupDwellTimerRef.current = null;
              setGroupHoverTarget(occupantId);
              if (navigator.vibrate) navigator.vibrate(30);
            }, dwellTime);
          } else if (!occupantId) {
            // Empty cell — clear dwell
            if (groupDwellTimerRef.current) {
              clearTimeout(groupDwellTimerRef.current);
              groupDwellTimerRef.current = null;
            }
            groupDwellCellRef.current = null;
            setGroupHoverTarget(null);
          }
        }

        // Page edge scrolling
        const screenWidth = window.innerWidth;
        const inLeftEdge = pos.x < PAGE_EDGE_ZONE;
        const inRightEdge = pos.x > screenWidth - PAGE_EDGE_ZONE;
        const cp = currentPageRef.current;

        if (inLeftEdge && cp > 0) {
          if (edgeDirection.current !== "left") {
            clearEdgeScroll();
            edgeDirection.current = "left";
            edgeScrollTimer.current = setTimeout(() => {
              setCurrentPage((p: number) => Math.max(0, p - 1));
              edgeScrollTimer.current = null;
              edgeDirection.current = null;
            }, PAGE_EDGE_DWELL);
          }
        } else if (inRightEdge && cp < totalPages - 1) {
          if (edgeDirection.current !== "right") {
            clearEdgeScroll();
            edgeDirection.current = "right";
            edgeScrollTimer.current = setTimeout(() => {
              setCurrentPage((p: number) => p + 1);
              edgeScrollTimer.current = null;
              edgeDirection.current = null;
            }, PAGE_EDGE_DWELL);
          }
        } else {
          clearEdgeScroll();
        }
      });
    },
    [
      cols,
      rows,
      totalPages,
      clearLongPress,
      clearEdgeScroll,
      setCurrentPage,
      updateGhostPosition,
    ]
  );

  const finalizeDrop = useCallback(() => {
    const active = activeDragRef.current;
    if (!active) return;

    const { iconId: dragId } = active;

    // Check if we should create/add to a group instead of normal placement
    if (groupHoverTarget) {
      const groups = iconGroupsRef.current ?? {};
      const isTargetGroup = !!groups[groupHoverTarget];

      if (isTargetGroup && onAddToGroup) {
        // Drop on existing group → add to group
        onAddToGroup(groupHoverTarget, dragId);
      } else if (onCreateGroup && hoverCell) {
        // Drop on regular icon → create new group
        onCreateGroup(dragId, groupHoverTarget, {
          type: "grid",
          page: hoverCell.page,
          row: hoverCell.row,
          col: hoverCell.col,
        });
      } else if (onCreateGroup && hoverDockCol !== null) {
        // Drop on dock icon → create group in dock
        onCreateGroup(dragId, groupHoverTarget, {
          type: "dock",
          col: hoverDockCol,
        });
      }
    } else if (hoverDockCol !== null) {
      // Normal drop on dock
      onPlaceInDock(dragId, hoverDockCol);
    } else if (hoverCell !== null) {
      // Normal drop on grid cell
      onPlaceInGrid(dragId, hoverCell.page, hoverCell.row, hoverCell.col);
    }

    // Clear group dwell state
    clearGroupDwell();
    activeDragRef.current = null;
    setIsSnapping(false);
    setDraggedIconId(null);
    setGhostInitial(null);
    setHoverCell(null);
    setHoverDockCol(null);
    setDragSource(null);
  }, [
    hoverDockCol,
    hoverCell,
    groupHoverTarget,
    onPlaceInGrid,
    onPlaceInDock,
    onCreateGroup,
    onAddToGroup,
    clearGroupDwell,
  ]);

  const handleTouchEnd = useCallback(() => {
    // If context menu was shown but jiggle hasn't started, cancel jiggle timer
    // so the context menu stays open for the user to interact with
    if (contextMenuShownRef.current && !isJiggling) {
      if (jiggleTimer.current) {
        clearTimeout(jiggleTimer.current);
        jiggleTimer.current = null;
      }
      contextMenuShownRef.current = false;
      return;
    }
    clearLongPress();
    clearEdgeScroll();
    // Don't clear groupHoverTarget here — finalizeDrop needs it
    if (groupDwellTimerRef.current) {
      clearTimeout(groupDwellTimerRef.current);
      groupDwellTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const active = activeDragRef.current;
    if (!active) return;

    // Snap ghost to target cell position, then finalize
    const ghost = ghostRef.current;
    const gridRect = gridRectRef.current;
    const dockRect = dockRectRef.current;

    if (ghost && (gridRect || dockRect)) {
      let targetX: number;
      let targetY: number;

      if (hoverDockCol !== null && dockRect) {
        // Snap to dock cell center
        const cellWidth = dockRect.width / cols;
        targetX = dockRect.left + hoverDockCol * cellWidth + cellWidth / 2;
        targetY = dockRect.top + dockRect.height / 2;
      } else if (hoverCell !== null && gridRect) {
        // Snap to grid cell
        const cellWidth = gridRect.width / cols;
        const cellHeight = gridRect.height / rows;
        targetX = gridRect.left + hoverCell.col * cellWidth + cellWidth / 2;
        targetY = gridRect.top + hoverCell.row * cellHeight + cellHeight / 2;
      } else {
        // No valid target — just finalize immediately
        finalizeDrop();
        return;
      }

      // Animate ghost to target
      setIsSnapping(true);
      ghost.style.transition = "transform 0.15s ease-out";
      ghost.style.transform = `translate(${targetX}px, ${targetY}px) translate(-50%, -50%) scale(1)`;

      // After animation, commit the reorder
      setTimeout(() => {
        ghost.style.transition = "";
        finalizeDrop();
      }, 150);
    } else {
      finalizeDrop();
    }
  }, [
    isJiggling,
    hoverDockCol,
    hoverCell,
    cols,
    rows,
    clearLongPress,
    clearEdgeScroll,
    finalizeDrop,
  ]);

  const handleBackgroundTap = useCallback(() => {
    if (isJiggling) {
      exitJiggleMode();
    }
  }, [isJiggling, exitJiggleMode]);

  return {
    isJiggling,
    draggedIconId,
    hoverCell,
    hoverDockCol,
    dragSource,
    ghostRef,
    ghostInitial,
    isSnapping,
    exitJiggleMode,
    handleIconTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleBackgroundTap,
    groupHoverTarget,
  };
}
